import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { api } from '../services/api';

const DIAS_JS = [
  { v: 0, l: 'D' }, { v: 1, l: 'S' }, { v: 2, l: 'T' }, { v: 3, l: 'Q' },
  { v: 4, l: 'Q' }, { v: 5, l: 'S' }, { v: 6, l: 'S' },
];
const DIAS_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const EMPTY = {
  titulo: '',
  descricao: '',
  data_inicio: '',
  data_fim: '',
  hora_inicio: '',
  hora_fim: '',
  local: '',
  endereco_completo: '',
  online: false,
  link_online: '',
  gratuito: true,
  valor: '',
  vagas: 0,
  imagem_url: '',
  recorrencia_tipo: 'none', // none | semanal | mensal | datas_especificas
  dias_semana: [],
  dia_mes: '',
  semana_do_mes: '',
  dia_semana_mensal: '',
  datas_especificas: [],
  data_fim_recorrencia: '',
  notificar_membros: false,
};

export default function EventoColetivoForm({ coletivoId, evento = null, onClose, onSaved }) {
  const isEdit = !!evento;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (evento) {
      const rec = evento.recorrencia || {};
      setForm({
        ...EMPTY,
        titulo: evento.titulo || '',
        descricao: evento.descricao || '',
        data_inicio: evento.data_inicio || '',
        data_fim: evento.data_fim || '',
        hora_inicio: evento.hora_inicio || '',
        hora_fim: evento.hora_fim || '',
        local: evento.local || '',
        endereco_completo: evento.endereco_completo || '',
        online: !!evento.online,
        link_online: evento.link_online || '',
        gratuito: evento.gratuito !== false,
        valor: evento.valor || '',
        vagas: evento.vagas || 0,
        imagem_url: evento.imagem_url || '',
        recorrencia_tipo: rec.tipo || 'none',
        dias_semana: rec.dias_semana || [],
        dia_mes: rec.dia_mes || '',
        semana_do_mes: rec.semana_do_mes || '',
        dia_semana_mensal: rec.dia_semana !== undefined ? rec.dia_semana : '',
        datas_especificas: rec.datas || [],
        data_fim_recorrencia: rec.data_fim_recorrencia || '',
        notificar_membros: false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [evento]);

  function up(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleDiaSemana(v) {
    setForm((f) => ({
      ...f,
      dias_semana: f.dias_semana.includes(v) ? f.dias_semana.filter((x) => x !== v) : [...f.dias_semana, v].sort(),
    }));
  }

  function addDataEspecifica() {
    setForm((f) => ({ ...f, datas_especificas: [...f.datas_especificas, ''] }));
  }
  function updateDataEspecifica(i, v) {
    setForm((f) => ({
      ...f,
      datas_especificas: f.datas_especificas.map((d, idx) => (idx === i ? v : d)),
    }));
  }
  function removeDataEspecifica(i) {
    setForm((f) => ({
      ...f,
      datas_especificas: f.datas_especificas.filter((_, idx) => idx !== i),
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.titulo.trim()) {
      toast.error('Título obrigatório');
      return;
    }
    if (form.recorrencia_tipo === 'none' && !form.data_inicio) {
      toast.error('Data de início obrigatória para evento único');
      return;
    }
    if (form.recorrencia_tipo === 'semanal' && form.dias_semana.length === 0) {
      toast.error('Selecione pelo menos um dia da semana');
      return;
    }
    if (form.recorrencia_tipo === 'mensal' && !form.dia_mes && form.semana_do_mes === '') {
      toast.error('Defina o dia do mês OU a semana+dia');
      return;
    }
    if (form.recorrencia_tipo === 'datas_especificas' && form.datas_especificas.filter(Boolean).length === 0) {
      toast.error('Adicione pelo menos uma data');
      return;
    }

    const recorrencia = (() => {
      if (form.recorrencia_tipo === 'none') return null;
      if (form.recorrencia_tipo === 'semanal') {
        return {
          tipo: 'semanal',
          dias_semana: form.dias_semana,
          data_fim_recorrencia: form.data_fim_recorrencia || null,
        };
      }
      if (form.recorrencia_tipo === 'mensal') {
        const r = { tipo: 'mensal', data_fim_recorrencia: form.data_fim_recorrencia || null };
        if (form.dia_mes) r.dia_mes = Number(form.dia_mes);
        else {
          r.semana_do_mes = Number(form.semana_do_mes);
          r.dia_semana = Number(form.dia_semana_mensal);
        }
        return r;
      }
      if (form.recorrencia_tipo === 'datas_especificas') {
        return {
          tipo: 'datas_especificas',
          datas: form.datas_especificas.filter(Boolean),
        };
      }
      return null;
    })();

    const payload = {
      titulo: form.titulo.trim(),
      descricao: form.descricao,
      data_inicio: form.data_inicio || null,
      data_fim: form.data_fim || null,
      hora_inicio: form.hora_inicio || null,
      hora_fim: form.hora_fim || null,
      local: form.local,
      endereco_completo: form.endereco_completo,
      online: form.online,
      link_online: form.online ? form.link_online : null,
      gratuito: form.gratuito,
      valor: form.gratuito ? null : Number(form.valor) || null,
      vagas: Number(form.vagas) || 0,
      imagem_url: form.imagem_url || null,
      recorrencia,
      notificar_membros: form.notificar_membros,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/v2/coletivos/${coletivoId}/eventos/${evento.id}`, payload);
        toast.success('Evento atualizado');
      } else {
        await api.post(`/v2/coletivos/${coletivoId}/eventos`, payload);
        toast.success('Evento criado');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="evento-form-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-bold text-lg">{isEdit ? 'Editar evento' : 'Novo evento'}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100" data-testid="evento-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <Label>Título *</Label>
            <Input value={form.titulo} onChange={(e) => up('titulo', e.target.value)} data-testid="evento-titulo" />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea rows={2} value={form.descricao} onChange={(e) => up('descricao', e.target.value)} data-testid="evento-descricao" />
          </div>

          {/* Recorrência */}
          <div className="border-t pt-3">
            <Label className="mb-2 block">Tipo de evento</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {[
                { v: 'none', l: 'Único' },
                { v: 'semanal', l: 'Semanal' },
                { v: 'mensal', l: 'Mensal' },
                { v: 'datas_especificas', l: 'Datas específicas' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => up('recorrencia_tipo', opt.v)}
                  data-testid={`evento-rec-${opt.v}`}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition ${
                    form.recorrencia_tipo === opt.v
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            {form.recorrencia_tipo === 'none' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Data de início *</Label>
                  <Input type="date" value={form.data_inicio} onChange={(e) => up('data_inicio', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Data de fim</Label>
                  <Input type="date" value={form.data_fim} onChange={(e) => up('data_fim', e.target.value)} />
                </div>
              </div>
            )}

            {form.recorrencia_tipo === 'semanal' && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs mb-1 block">Dias da semana</Label>
                  <div className="flex gap-1">
                    {DIAS_JS.map((d) => (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => toggleDiaSemana(d.v)}
                        data-testid={`evento-dia-${d.v}`}
                        className={`w-9 h-9 rounded-full text-sm font-medium transition ${
                          form.dias_semana.includes(d.v)
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                        }`}
                      >
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Até quando se repete (opcional)</Label>
                  <Input type="date" value={form.data_fim_recorrencia} onChange={(e) => up('data_fim_recorrencia', e.target.value)} />
                </div>
              </div>
            )}

            {form.recorrencia_tipo === 'mensal' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Dia fixo do mês (1–31)</Label>
                  <Input type="number" min="1" max="31" value={form.dia_mes} onChange={(e) => { up('dia_mes', e.target.value); up('semana_do_mes', ''); up('dia_semana_mensal', ''); }} placeholder="ex: 15" />
                </div>
                <div className="text-xs text-gray-500 text-center">— OU —</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Semana</Label>
                    <select value={form.semana_do_mes} onChange={(e) => { up('semana_do_mes', e.target.value); up('dia_mes', ''); }} className="w-full h-9 border rounded-md px-2 text-sm">
                      <option value="">Selecione…</option>
                      <option value="1">1ª</option>
                      <option value="2">2ª</option>
                      <option value="3">3ª</option>
                      <option value="4">4ª</option>
                      <option value="5">Última</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Dia da semana</Label>
                    <select value={form.dia_semana_mensal} onChange={(e) => { up('dia_semana_mensal', e.target.value); up('dia_mes', ''); }} className="w-full h-9 border rounded-md px-2 text-sm">
                      <option value="">Selecione…</option>
                      {DIAS_FULL.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Até quando se repete (opcional)</Label>
                  <Input type="date" value={form.data_fim_recorrencia} onChange={(e) => up('data_fim_recorrencia', e.target.value)} />
                </div>
              </div>
            )}

            {form.recorrencia_tipo === 'datas_especificas' && (
              <div className="space-y-2">
                {form.datas_especificas.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <Input type="date" value={d} onChange={(e) => updateDataEspecifica(i, e.target.value)} />
                    <Button type="button" variant="outline" size="sm" onClick={() => removeDataEspecifica(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addDataEspecifica} data-testid="evento-add-data">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar data
                </Button>
              </div>
            )}
          </div>

          {/* Horário */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Hora início</Label>
              <Input type="time" value={form.hora_inicio} onChange={(e) => up('hora_inicio', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Hora fim</Label>
              <Input type="time" value={form.hora_fim} onChange={(e) => up('hora_fim', e.target.value)} />
            </div>
          </div>

          {/* Local */}
          <div>
            <Label className="text-xs">Local</Label>
            <Input value={form.local} onChange={(e) => up('local', e.target.value)} placeholder="ex: Praça Central" />
          </div>
          <div>
            <Label className="text-xs">Endereço completo</Label>
            <Input value={form.endereco_completo} onChange={(e) => up('endereco_completo', e.target.value)} />
          </div>

          {/* Online */}
          <div className="flex items-center gap-3">
            <Switch checked={form.online} onCheckedChange={(v) => up('online', v)} />
            <Label className="text-sm">Evento online</Label>
          </div>
          {form.online && (
            <Input value={form.link_online} onChange={(e) => up('link_online', e.target.value)} placeholder="Link da reunião/transmissão" />
          )}

          {/* Vagas e preço */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Vagas</Label>
              <Input type="number" min="0" value={form.vagas} onChange={(e) => up('vagas', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Gratuito?</Label>
              <div className="flex items-center h-9 gap-2">
                <Switch checked={form.gratuito} onCheckedChange={(v) => up('gratuito', v)} />
                <span className="text-xs">{form.gratuito ? 'Sim' : 'Pago'}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Valor (R$)</Label>
              <Input type="number" min="0" step="0.01" value={form.valor} disabled={form.gratuito} onChange={(e) => up('valor', e.target.value)} />
            </div>
          </div>

          {/* Notificar membros */}
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
            <Switch checked={form.notificar_membros} onCheckedChange={(v) => up('notificar_membros', v)} data-testid="evento-notificar" />
            <div className="text-xs">
              <div className="font-medium text-purple-900">Notificar membros por e-mail</div>
              <div className="text-purple-700/80">Enviar aviso a todos os negócios aprovados deste coletivo sobre o novo evento.</div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="evento-form-submit">
              {saving ? 'Salvando…' : isEdit ? 'Atualizar' : 'Criar evento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
